import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';

// Admin: Daily Social Post generator. Each day a branded 1080×1080 graphic + an
// Agra-tuned AI caption is produced for Instagram/WhatsApp/Facebook. Admin reviews,
// tweaks the caption, downloads the graphic, and posts. Backend: socialPostService.

const LANraw = ['hinglish', 'hindi', 'english'];

function DailySocialPost() {
  const [post, setPost] = useState(null);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [aiImageConfigured, setAiImageConfigured] = useState(false);
  const [imageProviders, setImageProviders] = useState({ openai: false, replicate: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [refUrl, setRefUrl] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, h] = await Promise.all([
        adminAPI.getDailySocialToday(),
        adminAPI.getDailySocialHistory({ limit: 60 }),
      ]);
      const p = t.data?.data?.post || null;
      setPost(p);
      setSettings(t.data?.data?.settings || {});
      setAiImageConfigured(!!t.data?.data?.aiImageConfigured);
      setImageProviders(t.data?.data?.imageProviders || { openai: false, replicate: false });
      setCaption(p?.caption || '');
      setTags((p?.hashtags || []).join(' '));
      setHistory(h.data?.data?.posts || []);
      setErr('');
    } catch (e) {
      setErr('Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const generate = async (force) => {
    setBusy(force ? 'regen' : 'gen'); setErr('');
    try {
      const r = await adminAPI.generateDailySocialPost({ force });
      const p = r.data?.data?.post;
      setPost(p); setCaption(p?.caption || ''); setTags((p?.hashtags || []).join(' '));
      flash(force ? 'Regenerated ✓' : 'Generated ✓');
      adminAPI.getDailySocialHistory({ limit: 60 }).then(h => setHistory(h.data?.data?.posts || []));
    } catch (e) {
      setErr(e.response?.data?.message || 'Generation failed');
    } finally { setBusy(''); }
  };

  const saveCaption = async () => {
    if (!post) return;
    setBusy('save');
    try {
      await adminAPI.updateDailySocialPost(post._id, { caption, hashtags: tags.split(/\s+/).filter(Boolean) });
      flash('Caption saved ✓');
    } catch (e) { setErr('Save failed'); } finally { setBusy(''); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); flash((label || 'Copied') + ' ✓'); }
    catch { setErr('Copy failed — select & copy manually'); }
  };

  const download = async (url, date) => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `foodwinc-social-${date || 'post'}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch { window.open(url, '_blank'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await adminAPI.deleteDailySocialPost(id); await load(); } catch { setErr('Delete failed'); }
  };

  const saveSettings = async () => {
    setBusy('settings');
    try {
      const payload = {
        ...settings,
        backgroundImages: String(settings.backgroundImages || '')
          .split(/[\n,]+/).map(s => s.trim()).filter(Boolean),
      };
      // backgroundImages may already be an array when loaded; normalize
      if (Array.isArray(settings.backgroundImages)) payload.backgroundImages = settings.backgroundImages;
      const r = await adminAPI.updateDailySocialSettings(payload);
      setSettings(r.data?.data?.settings || settings);
      flash('Settings saved ✓');
    } catch (e) { setErr('Settings save failed'); } finally { setBusy(''); }
  };

  const saveRefs = async (newList) => {
    setS('referenceImages', newList);
    try { await adminAPI.updateDailySocialSettings({ referenceImages: newList }); flash('References updated ✓'); }
    catch { setErr('Failed to save references'); }
  };
  const addRefUrl = async () => {
    const u = refUrl.trim();
    if (!/^https?:\/\//i.test(u)) { setErr('Enter a valid https image URL'); return; }
    await saveRefs([...(settings?.referenceImages || []), u]);
    setRefUrl('');
  };
  const uploadRef = async (file) => {
    if (!file) return;
    setBusy('ref');
    try {
      const fd = new FormData(); fd.append('image', file);
      const r = await adminAPI.uploadDailySocialReference(fd);
      const url = r.data?.data?.url;
      if (url) await saveRefs([...(settings?.referenceImages || []), url]);
    } catch (e) { setErr('Upload failed: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(''); }
  };
  const removeRef = (u) => saveRefs((settings?.referenceImages || []).filter(x => x !== u));

  const setS = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const captionFull = (p, c, tg) => `${c || ''}\n\n${(typeof tg === 'string' ? tg : (p?.hashtags || []).join(' '))}`.trim();
  const bgText = Array.isArray(settings?.backgroundImages) ? settings.backgroundImages.join('\n') : (settings?.backgroundImages || '');

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: 0 }}>📣 Daily Social Post</h1>
      <p style={{ color: '#64748B', fontSize: 14, margin: '6px 0 16px' }}>
        A branded post graphic + caption for FoodWinc, tuned for Agra — made fresh each day. Review, tweak the caption, download the image, and post to Instagram / WhatsApp / Facebook.
      </p>
      {msg ? <div style={bar('#F0FDF4', '#166534')}>{msg}</div> : null}
      {err ? <div style={bar('#FEF2F2', '#B91C1C')}>{err}</div> : null}

      {loading ? <p style={{ color: '#64748B' }}>Loading…</p> : (
        <>
          {/* Today's post */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#0F172A' }}>Today’s post</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                {post && post.status === 'ready'
                  ? <button onClick={() => generate(true)} disabled={!!busy} style={btn('#6366F1')}>{busy === 'regen' ? '…' : '↻ Regenerate'}</button>
                  : <button onClick={() => generate(false)} disabled={!!busy} style={btn('#16A34A')}>{busy === 'gen' ? 'Generating…' : '✨ Generate today’s post'}</button>}
              </div>
            </div>

            {post && post.status === 'ready' ? (
              <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 340px' }}>
                  <img src={post.imageUrl} alt="Daily FoodWinc post" style={{ width: 340, height: 340, borderRadius: 12, objectFit: 'cover', border: '1px solid #E2E8F0' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={() => download(post.imageUrl, post.date)} style={btn('#0F172A', true)}>⬇ Download image</button>
                    <button onClick={() => window.open(post.imageUrl, '_blank')} style={btn('#64748B', true)}>Open</button>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 300 }}>
                  <label style={lbl}>Caption</label>
                  <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={6} style={ta} />
                  <label style={lbl}>Hashtags</label>
                  <textarea value={tags} onChange={e => setTags(e.target.value)} rows={2} style={ta} />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    <button onClick={saveCaption} disabled={!!busy} style={btn('#16A34A', true)}>{busy === 'save' ? '…' : '💾 Save edits'}</button>
                    <button onClick={() => copy(captionFull(post, caption, tags), 'Caption + tags copied')} style={btn('#2563EB', true)}>📋 Copy caption + tags</button>
                    <button onClick={() => copy(caption, 'Caption copied')} style={btn('#64748B', true)}>Copy caption only</button>
                  </div>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 10 }}>
                    {post.theme ? <>Angle: <b>{post.theme}</b> · </> : null}
                    {post.aiModel ? 'AI copy' : 'template copy'} · {post.generatedBy === 'auto' ? 'auto' : 'manual'} · {post.date}
                  </div>
                </div>
              </div>
            ) : post && post.status === 'failed' ? (
              <p style={{ color: '#B91C1C', marginTop: 12 }}>Last attempt failed: {post.error || 'unknown error'}. Try Generate again.</p>
            ) : (
              <p style={{ color: '#64748B', marginTop: 12 }}>No post for today yet. Click “Generate” to create one now (or turn on auto-generate below).</p>
            )}
          </div>

          {/* Settings */}
          {settings && (
            <div style={card}>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 12px', color: '#0F172A' }}>⚙️ Auto-generate & style</h2>

              {/* AI image generation */}
              <div style={{ padding: '10px 12px', borderRadius: 10, marginBottom: 14, background: (settings.aiImageEnabled !== false && aiImageConfigured) ? '#F0FDF4' : '#FFF7ED', border: '1px solid #E2E8F0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700 }}>
                    <input type="checkbox" checked={settings.aiImageEnabled !== false} onChange={e => setS('aiImageEnabled', e.target.checked)} />
                    🎨 AI-generate the image itself
                  </label>
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Model:
                    <select style={{ ...inp, padding: '5px 8px' }} value={settings.aiImageProvider || 'auto'} onChange={e => setS('aiImageProvider', e.target.value)}>
                      <option value="auto">Auto (best available)</option>
                      <option value="openai">ChatGPT (OpenAI gpt-image-1)</option>
                      <option value="replicate">Replicate (Flux)</option>
                    </select>
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <span style={pbadge(imageProviders.openai)}>ChatGPT {imageProviders.openai ? '✓' : '✕'}</span>
                  <span style={pbadge(imageProviders.replicate)}>Replicate {imageProviders.replicate ? '✓' : '✕'}</span>
                  <span style={{ fontSize: 12, color: '#64748B', flex: 1, minWidth: 220 }}>
                    {aiImageConfigured
                      ? 'Each post gets a fresh AI food image — no photo pool needed. ChatGPT gives the best food realism.'
                      : 'No image model configured — add OPENAI_API_KEY (ChatGPT) or REPLICATE_API_TOKEN to the backend. Until then it uses the photo pool / gradient below.'}
                  </span>
                </div>
              </div>
              <Field label="AI image style (optional)"><input style={{ ...inp, minWidth: 360 }} value={settings.aiImageStyle || ''} onChange={e => setS('aiImageStyle', e.target.value)} placeholder="e.g. top-down, dark moody background, street-food stall" /></Field>

              {/* Image references — Claude studies these to match the TYPE of image + caption */}
              <div style={{ marginTop: 14, padding: '12px 14px', border: '1px dashed #CBD5E1', borderRadius: 10, background: '#F8FAFC' }}>
                <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 14 }}>🖼️ Image references</div>
                <p style={{ fontSize: 12, color: '#64748B', margin: '4px 0 10px' }}>
                  Add sample images (the kind of food photo you want). The AI studies them to understand <b>what type of image to generate</b> and writes a matching caption. Upload or paste image URLs.
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                  {(settings.referenceImages || []).map(u => (
                    <div key={u} style={{ position: 'relative', width: 88, height: 88 }}>
                      <img src={u} alt="reference" style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 8, border: '1px solid #E2E8F0' }} />
                      <button onClick={() => removeRef(u)} title="Remove" style={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: 11, border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  {(settings.referenceImages || []).length === 0 ? <span style={{ fontSize: 13, color: '#94A3B8' }}>No references yet — the AI will invent the food subject on its own.</span> : null}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <label style={{ ...btn('#0F172A', true), display: 'inline-block' }}>
                    {busy === 'ref' ? 'Uploading…' : '⬆ Upload image'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} disabled={busy === 'ref'} onChange={e => { uploadRef(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                  </label>
                  <input style={{ ...inp, minWidth: 260, flex: 1 }} value={refUrl} onChange={e => setRefUrl(e.target.value)} placeholder="…or paste an image URL (https://…)" />
                  <button onClick={addRefUrl} disabled={!refUrl.trim()} style={btn(refUrl.trim() ? '#16A34A' : '#94A3B8', true)}>Add URL</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
                  <input type="checkbox" checked={!!settings.enabled} onChange={e => setS('enabled', e.target.checked)} />
                  Auto-generate daily
                </label>
                <Field label="Time (IST)"><input style={inp} value={settings.timeIST || '09:00'} onChange={e => setS('timeIST', e.target.value)} placeholder="09:00" /></Field>
                <Field label="Window (min)"><input style={inp} type="number" value={settings.windowMinutes ?? 30} onChange={e => setS('windowMinutes', e.target.value)} /></Field>
                <Field label="City"><input style={inp} value={settings.city || 'Agra'} onChange={e => setS('city', e.target.value)} /></Field>
                <Field label="Language">
                  <select style={inp} value={settings.language || 'hinglish'} onChange={e => setS('language', e.target.value)}>
                    {LANraw.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Handle"><input style={inp} value={settings.handle || '@foodwinc'} onChange={e => setS('handle', e.target.value)} /></Field>
                <Field label="Website"><input style={inp} value={settings.website || 'foodwinc.com'} onChange={e => setS('website', e.target.value)} /></Field>
                <Field label="Brand color"><input style={{ ...inp, width: 70, padding: 2, height: 36 }} type="color" value={settings.brandColor || '#FF6961'} onChange={e => setS('brandColor', e.target.value)} /></Field>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
                  <input type="checkbox" checked={settings.includeOffer !== false} onChange={e => setS('includeOffer', e.target.checked)} />
                  Allow savings/offer badge
                </label>
              </div>
              <Field label="Tone / genre (optional)"><input style={{ ...inp, minWidth: 360 }} value={settings.genre || ''} onChange={e => setS('genre', e.target.value)} placeholder="e.g. witty desi, foodgasm, filmy…" /></Field>
              <div style={{ marginTop: 10 }}>
                <label style={lbl}>Background food photos — fallback for when AI image is OFF/unconfigured (one https URL per line; rotates; else Daily-Nudge craving photos, then a brand gradient)</label>
                <textarea
                  rows={3}
                  style={ta}
                  value={bgText}
                  onChange={e => setS('backgroundImages', e.target.value.split(/[\n,]+/).map(s => s.trim()).filter(Boolean))}
                  placeholder="https://…/food1.jpg&#10;https://…/food2.jpg"
                />
              </div>
              <button onClick={saveSettings} disabled={!!busy} style={{ ...btn('#16A34A'), marginTop: 12 }}>{busy === 'settings' ? '…' : 'Save settings'}</button>
            </div>
          )}

          {/* History */}
          <div style={card}>
            <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 12px', color: '#0F172A' }}>🗂️ Recent posts</h2>
            {history.length === 0 ? <p style={{ color: '#94A3B8' }}>No posts yet.</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {history.filter(h => h.imageUrl).map(h => (
                  <div key={h._id} style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                    <img src={h.imageUrl} alt={h.date} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                    <div style={{ padding: 8 }}>
                      <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>{h.date}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button onClick={() => copy(captionFull(h, h.caption), 'Copied')} style={mini('#2563EB')}>Copy</button>
                        <button onClick={() => download(h.imageUrl, h.date)} style={mini('#0F172A')}>⬇</button>
                        <button onClick={() => del(h._id)} style={mini('#EF4444')}>✕</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reels — brand-aligned vertical video generator */}
          <ReelStudio aiImageConfigured={aiImageConfigured} flash={flash} setErr={setErr} copy={copy} />
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------
// ReelStudio — generates a vertical 1080×1920 MP4 reel from the SAME brand profile:
// Claude writes a storyboard, each scene becomes a branded frame over a real food
// photo, ffmpeg stitches them with Ken-Burns motion + crossfades. Self-contained so
// it never touches the daily-post state above. Free (no per-use cost beyond Claude).
// ------------------------------------------------------------------------------------
function ReelStudio({ aiImageConfigured, flash, setErr, copy }) {
  const [reels, setReels] = useState([]);
  const [cfg, setCfg] = useState({ enabled: false, sceneCount: 4, secondsPerScene: 2.5, musicUrl: '' });
  const [busy, setBusy] = useState('');
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminAPI.getSocialReels({ limit: 40 });
      setReels(r.data?.data?.reels || []);
      const s = r.data?.data?.settings || {};
      setCfg({
        enabled: !!s.enabled,
        sceneCount: s.sceneCount || 4,
        secondsPerScene: s.secondsPerScene || 2.5,
        musicUrl: s.musicUrl || '',
      });
      setLoaded(true);
    } catch { /* non-fatal — page still works without reels */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-poll while any reel is still rendering ('pending') so the gallery flips to
  // 'ready'/'failed' on its own — rendering takes a few minutes on the server.
  const hasPending = reels.some(r => r.status === 'pending');
  useEffect(() => {
    if (!hasPending) return undefined;
    const t = setInterval(() => { load(); }, 6000);
    return () => clearInterval(t);
  }, [hasPending, load]);

  const setC = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const saveCfg = async () => {
    setBusy('cfg');
    try { await adminAPI.updateSocialReelSettings(cfg); flash('Reel settings saved ✓'); }
    catch { setErr('Reel settings save failed'); } finally { setBusy(''); }
  };

  const generate = async () => {
    setBusy('gen'); setErr('');
    try {
      await adminAPI.generateSocialReel();
      flash('Reel started — rendering in the background (a few minutes). It will appear below automatically.');
      await load(); // shows the new 'pending' card; auto-poll takes over from here
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to start reel — try again');
    } finally { setBusy(''); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this reel?')) return;
    try { await adminAPI.deleteSocialReel(id); await load(); } catch { setErr('Delete failed'); }
  };

  const downloadVideo = async (url, id) => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `foodwinc-reel-${id}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch { window.open(url, '_blank'); }
  };

  const capFull = (r) => `${r.caption || ''}\n\n${(r.hashtags || []).join(' ')}`.trim();

  return (
    <>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#0F172A' }}>🎬 Reels</h2>
            <p style={{ color: '#64748B', fontSize: 13, margin: '4px 0 0' }}>
              A vertical 1080×1920 video built from the SAME brand profile — Claude writes the storyboard, your real food photos become branded scenes, stitched with motion + crossfades. Download &amp; post to Reels / Shorts / Status.
            </p>
          </div>
          <button onClick={generate} disabled={!!busy} style={btn('#DB2777')}>{busy === 'gen' ? 'Starting…' : '🎬 Generate a reel'}</button>
        </div>

        {!aiImageConfigured ? (
          <div style={{ ...bar('#FFF7ED', '#9A3412'), marginTop: 12, marginBottom: 0 }}>
            No AI image model configured — reels will use your <b>Background food photos</b> pool above (add real dish photos there for the most realistic result), else a brand gradient.
          </div>
        ) : null}

        {/* Reel settings */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
            <input type="checkbox" checked={!!cfg.enabled} onChange={e => setC('enabled', e.target.checked)} />
            Enable reels
          </label>
          <Field label="Scenes (3–6)"><input style={{ ...inp, width: 80 }} type="number" min={3} max={6} value={cfg.sceneCount} onChange={e => setC('sceneCount', e.target.value)} /></Field>
          <Field label="Seconds / scene"><input style={{ ...inp, width: 90 }} type="number" min={1.5} max={5} step={0.5} value={cfg.secondsPerScene} onChange={e => setC('secondsPerScene', e.target.value)} /></Field>
          <Field label="Music URL (optional, royalty-free mp3 you're licensed for)"><input style={{ ...inp, minWidth: 320 }} value={cfg.musicUrl} onChange={e => setC('musicUrl', e.target.value)} placeholder="https://…/track.mp3  (blank = silent, add music in-app)" /></Field>
          <button onClick={saveCfg} disabled={!!busy} style={btn('#16A34A', true)}>{busy === 'cfg' ? '…' : 'Save reel settings'}</button>
        </div>
      </div>

      {/* Reel gallery */}
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 12px', color: '#0F172A' }}>🗂️ Recent reels</h2>
        {!loaded ? <p style={{ color: '#94A3B8' }}>Loading…</p> : reels.length === 0 ? (
          <p style={{ color: '#94A3B8' }}>No reels yet — click “Generate a reel”. It takes ~30–90s to render.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {reels.map(r => (
              <div key={r._id} style={{ border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                {r.status === 'ready' && r.videoUrl ? (
                  <video src={r.videoUrl} poster={r.posterUrl || undefined} controls playsInline style={{ width: '100%', aspectRatio: '9 / 16', objectFit: 'cover', display: 'block', background: '#000' }} />
                ) : (
                  <div style={{ width: '100%', aspectRatio: '9 / 16', display: 'flex', alignItems: 'center', justifyContent: 'center', background: r.status === 'failed' ? '#FEF2F2' : '#F1F5F9', color: r.status === 'failed' ? '#B91C1C' : '#64748B', fontSize: 12, padding: 12, textAlign: 'center', whiteSpace: 'pre-line' }}>
                    {r.status === 'failed' ? ('Failed: ' + (r.error || 'unknown')) : '🎬 Rendering… (a few minutes)\nthis updates automatically'}
                  </div>
                )}
                <div style={{ padding: 10 }}>
                  <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>
                    {r.theme ? <>{r.theme} · </> : null}{r.durationSec ? `${r.durationSec}s` : ''}{r.aiModel ? ' · AI copy' : ''}
                  </div>
                  {r.caption ? <div style={{ fontSize: 12, color: '#334155', marginTop: 4, maxHeight: 54, overflow: 'hidden' }}>{r.caption}</div> : null}
                  {r.status === 'ready' ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button onClick={() => copy(capFull(r), 'Caption copied')} style={mini('#2563EB')}>Copy</button>
                      <button onClick={() => downloadVideo(r.videoUrl, r._id)} style={mini('#0F172A')}>⬇</button>
                      <button onClick={() => del(r._id)} style={mini('#EF4444')}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button onClick={() => del(r._id)} style={mini('#EF4444')}>✕ Delete</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <label style={lbl}>{label}</label>
    {children}
  </div>
);

const card = { border: '1px solid #E2E8F0', borderRadius: 14, padding: 18, marginBottom: 18, background: '#fff' };
const btn = (bg, small) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: small ? '8px 12px' : '10px 16px', fontSize: small ? 13 : 14, fontWeight: 600, cursor: 'pointer' });
const mini = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flex: 1 });
const inp = { padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const ta = { width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' };
const lbl = { fontSize: 12, color: '#64748B', fontWeight: 600, marginBottom: 2, display: 'block' };
const bar = (bg, color) => ({ background: bg, color, padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 12 });
const pbadge = (on) => ({ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: on ? '#DCFCE7' : '#FEE2E2', color: on ? '#166534' : '#B91C1C' });

export default DailySocialPost;
